import { Rocket, Radar, TrendingUp, Trophy, MousePointerClick, HandshakeIcon, Target, Zap } from "lucide-react";

const PHASES = [
    {
        id: "01",
        title: "Ignition",
        window: "Days 0 – 30",
        icon: Rocket,
        visits: {
            headline: "Foundation & first flywheel",
            actions: [
                "Google Ads + Meta campaigns in your top 3 micro-markets",
                "City-specific landing pages with local schema markup",
                "WhatsApp click-to-chat CTAs across all pages",
                "SEO audit + sitemap + Google Search Console setup",
            ],
        },
        sales: {
            headline: "Speed & structure",
            actions: [
                "Speed-to-lead automation: first response in <60 seconds",
                "BANT-based lead scoring (Budget · Authority · Need · Timeline)",
                "CRM integration + call-script library live",
                "Daily lead-quality feedback loop with your sales team",
            ],
        },
        kpi: "Campaigns live · speed-to-lead process & CRM integration in place",
    },
    {
        id: "02",
        title: "Amplification",
        window: "Days 31 – 90",
        icon: Radar,
        visits: {
            headline: "Expand the top of funnel",
            actions: [
                "YouTube pre-roll + programmatic display retargeting",
                "Content engine: 12 SEO articles/month on buyer intent keywords",
                "Referral tie-ups with mortgage brokers & CA networks",
                "Instagram Reels + short-form video walkthroughs",
            ],
        },
        sales: {
            headline: "Nurture what you have",
            actions: [
                "7-touch WhatsApp nurture sequences per persona",
                "Site-visit incentives (fuel voucher · weekend transport)",
                "Weekly sales coaching + recorded call reviews",
                "Objection-handling playbook v1 rolled out to team",
            ],
        },
        kpi: "Expanded top-of-funnel reach · nurture sequences running",
    },
    {
        id: "03",
        title: "Scale",
        window: "Days 91 – 180",
        icon: TrendingUp,
        visits: {
            headline: "Own the micro-market",
            actions: [
                "Preferred partnerships with Housing.com, 99acres, MagicBricks",
                "Influencer + local realtor collaborations",
                "Video project walkthroughs + drone tours + VR",
                "Community events, weekend site fests, expo presence",
            ],
        },
        sales: {
            headline: "Compound the conversions",
            actions: [
                "AI-powered predictive lead scoring model",
                "Sales playbook v2 with battle cards for competing projects",
                "Customer story library (video testimonials)",
                "Dedicated NRI desk for Gulf & USA buyers",
            ],
        },
        kpi: "Broader micro-market presence · partnership channels active",
    },
    {
        id: "04",
        title: "Compounding",
        window: "Days 181 – 365",
        icon: Trophy,
        visits: {
            headline: "Brand becomes the moat",
            actions: [
                "Brand-led OOH + PR in target metros",
                "Referral engine: existing buyers get ₹25k for every closure",
                "Broker network expansion (250+ empanelled brokers)",
                "Loyalty program for repeat buyers & investors",
            ],
        },
        sales: {
            headline: "Every ₹ traced, every deal repeatable",
            actions: [
                "Multi-touch attribution model live end-to-end",
                "Cross-project upsell + inventory-swap playbook",
                "Quarterly business review with C-suite",
                "Sales team certification & retention programme",
            ],
        },
        kpi: "Brand-led demand generation · referral engine established",
    },
];

const METRICS = [
    { icon: MousePointerClick, label: "Site visits", note: "Grows steadily as campaigns mature and reach expands" },
    { icon: Target, label: "Cost per lead", note: "Refined over time as targeting learns your market" },
    { icon: Zap, label: "Speed to first contact", note: "Automation shortens response time" },
    { icon: HandshakeIcon, label: "Site-visit to booking rate", note: "Improves as qualification gets sharper" },
];

export default function Roadmap() {
    return (
        <section
            id="roadmap"
            data-testid="roadmap-section"
            className="relative py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <div className="flex items-center gap-3 justify-center mb-6">
                        <div className="h-px w-10 bg-[#D4AF37]" />
                        <span className="overline">The 12-Month Growth Playbook</span>
                        <div className="h-px w-10 bg-[#D4AF37]" />
                    </div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        From launch to <span className="italic text-[#D4AF37]">market leadership</span> in four phases.
                    </h2>
                    <p className="mt-6 text-white/70 leading-relaxed">
                        A structured, phase-wise roadmap covering both traffic acquisition and sales conversion —
                        so you know exactly what happens in month 1 vs month 12.
                    </p>
                </div>

                {/* Outcome Metrics */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-24" data-testid="outcome-metrics">
                    {METRICS.map((m) => (
                        <div key={m.label} className="hol-card p-6">
                            <m.icon size={20} className="text-[#D4AF37]" strokeWidth={1.5} />
                            <div className="text-xs text-white/50 uppercase tracking-widest mt-4">{m.label}</div>
                            <div className="mt-3 text-[13px] text-white/70 leading-relaxed">{m.note}</div>
                        </div>
                    ))}
                </div>

                {/* Phases timeline */}
                <div className="relative">
                    {/* Gold vertical line on desktop */}
                    <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent -translate-x-1/2" />

                    <div className="space-y-16 lg:space-y-24">
                        {PHASES.map((p, i) => (
                            <div
                                key={p.id}
                                data-testid={`phase-${p.id}`}
                                className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-start ${
                                    i % 2 === 1 ? "lg:[direction:rtl]" : ""
                                }`}
                            >
                                {/* Left: Phase header */}
                                <div className={`${i % 2 === 1 ? "lg:[direction:ltr]" : ""} relative`}>
                                    <div className="flex items-start gap-6">
                                        <div className="shrink-0 h-16 w-16 border border-[#D4AF37] text-[#D4AF37] grid place-items-center bg-[#0A0B10]">
                                            <p.icon size={22} strokeWidth={1.5} />
                                        </div>
                                        <div>
                                            <div className="overline">Phase {p.id}</div>
                                            <h3 className="font-serif-display text-4xl sm:text-5xl mt-1">
                                                {p.title}
                                            </h3>
                                            <div className="mt-2 text-sm text-white/50 tracking-widest uppercase">
                                                {p.window}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-8 bg-[#D4AF37]/8 border-l-2 border-[#D4AF37] pl-5 py-4">
                                        <div className="overline !text-[#D4AF37]/80 mb-1">Phase KPI target</div>
                                        <div className="font-serif-display text-lg text-white leading-tight">
                                            {p.kpi}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: Two columns visits + sales */}
                                <div className={`space-y-6 ${i % 2 === 1 ? "lg:[direction:ltr]" : ""}`}>
                                    <TrackCard
                                        tag="Site-visit growth"
                                        headline={p.visits.headline}
                                        actions={p.visits.actions}
                                        testid={`phase-${p.id}-visits`}
                                    />
                                    <TrackCard
                                        tag="Sales conversion"
                                        headline={p.sales.headline}
                                        actions={p.sales.actions}
                                        gold
                                        testid={`phase-${p.id}-sales`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Final CTA */}
                <div className="mt-24 text-center border-t border-white/10 pt-16">
                    <p className="font-serif-display text-3xl sm:text-4xl max-w-2xl mx-auto leading-tight">
                        Ready to compress <span className="italic text-[#D4AF37]">12 months of growth</span> into your next quarter?
                    </p>
                    <a href="#lead" data-testid="roadmap-cta" className="btn-gold mt-8">
                        Build my roadmap
                    </a>
                </div>
            </div>
        </section>
    );
}

function TrackCard({ tag, headline, actions, gold, testid }) {
    return (
        <div
            data-testid={testid}
            className={`p-6 border ${gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#0A0B10]"}`}
        >
            <div className="flex items-center gap-2 mb-4">
                <div className={`h-1.5 w-1.5 ${gold ? "bg-[#D4AF37]" : "bg-white/40"}`} />
                <span className={`overline ${gold ? "!text-[#D4AF37]" : "!text-white/60"}`}>{tag}</span>
            </div>
            <h4 className="font-serif-display text-xl text-white">{headline}</h4>
            <ul className="mt-4 space-y-2.5">
                {actions.map((a) => (
                    <li key={a} className="flex items-start gap-2 text-sm text-white/75">
                        <span className={`mt-1.5 h-1 w-1 shrink-0 ${gold ? "bg-[#D4AF37]" : "bg-white/40"}`} />
                        <span>{a}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
